import api from './apiService';

export interface VoucherListData {
  success: boolean;
  message: string;
  data: {
    data: VoucherItem[];
    has_next: boolean;
    has_prev: boolean;
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

export interface VoucherItem {
  voucher_code: string;
  voucher_name: string;
  description: string;
  arena_id: number;
  usage_limit: number;
  used_count: number;
  is_active: boolean;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  max_discount_amount: number;
  valid_from: string;
  valid_until: string;
  max_usage_per_user: number;
  applicable_for: 'arena' | 'products' | 'both';
  min_transaction_amount: number;
  is_email_restricted: boolean;
  terms_and_conditions?: string | null;
  created_by: number;
  allowed_emails?: { voucher_id: number; email: string; is_active: boolean }[];
}

// {
//     "success": true,
//     "message": "Voucher retrieved successfully",
//     "data": {
//         "voucher_code": "UI123",
//         "voucher_name": "Test Voucher UI",
//         "description": "test",
//         "arena_id": 9,
//         "usage_limit": 100,
//         "used_count": 0,
//         "is_active": true,
//         "discount_type": "fixed",
//         "discount_value": 25000,
//         "max_discount_amount": 0,
//         "valid_from": "2025-11-01T07:00:00+07:00",
//         "valid_until": "2025-12-01T06:59:59+07:00",
//         "max_usage_per_user": 1,
//         "applicable_for": "arena",
//         "min_transaction_amount": 50000,
//         "is_email_restricted": true,
//         "terms_and_conditions": null,
//         "created_by": 69,
//         "allowed_emails": [
//             {
//                 "voucher_id": 6,
//                 "email": "agung.yudha007@gmail.com",
//                 "is_active": true,
//                 "voucher": {
//                     "voucher_code": "",
//                     "voucher_name": "",
//                     "description": null,
//                     "arena_id": null,
//                     "usage_limit": 0,
//                     "used_count": 0,
//                     "is_active": false,
//                     "discount_type": "",
//                     "discount_value": 0,
//                     "max_discount_amount": 0,
//                     "valid_from": "0001-01-01T00:00:00Z",
//                     "valid_until": "0001-01-01T00:00:00Z",
//                     "max_usage_per_user": 0,
//                     "applicable_for": "",
//                     "min_transaction_amount": 0,
//                     "is_email_restricted": false,
//                     "terms_and_conditions": null,
//                     "created_by": null
//                 }
//             },
//             {
//                 "voucher_id": 6,
//                 "email": "agung@mail.me",
//                 "is_active": true,
//                 "voucher": {
//                     "voucher_code": "",
//                     "voucher_name": "",
//                     "description": null,
//                     "arena_id": null,
//                     "usage_limit": 0,
//                     "used_count": 0,
//                     "is_active": false,
//                     "discount_type": "",
//                     "discount_value": 0,
//                     "max_discount_amount": 0,
//                     "valid_from": "0001-01-01T00:00:00Z",
//                     "valid_until": "0001-01-01T00:00:00Z",
//                     "max_usage_per_user": 0,
//                     "applicable_for": "",
//                     "min_transaction_amount": 0,
//                     "is_email_restricted": false,
//                     "terms_and_conditions": null,
//                     "created_by": null
//                 }
//             }
//         ]
//     }
// }

export interface DataDetailResponse {
  voucher_code: string;
  voucher_name: string;
  description: string;
  arena_id: number;
  usage_limit: number;
  used_count: number;
  is_active: boolean;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  max_discount_amount: number;
  valid_from: string;
  valid_until: string;
  max_usage_per_user: number;
  applicable_for: 'arena' | 'products' | 'both';
  min_transaction_amount: number;
  is_email_restricted: boolean;
  terms_and_conditions?: string | null;
  created_by: number;
  allowed_emails?: { voucher_id: number; email: string; is_active: boolean }[];
}

export interface VoucherDetailResponse {
  success: boolean;
  message: string;
  data?: DataDetailResponse;
}

export const getVouchers = async (
  page: number = 1,
  pageSize: number = 10,
  sortBy?: string,
  order?: 'asc' | 'desc'
): Promise<VoucherListData> => {
  const response = await api.get<VoucherListData>('/vouchers', {
    params: {
      page,
      page_size: pageSize,
      sort_by: sortBy,
      order,
    },
  });
  return response.data;
};

export const updateVoucherStatus = async (
  voucherCode: string,
  isActive: boolean
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.patch<{ success: boolean; message: string }>(
      `/vouchers/${voucherCode}/status`,
      { is_active: isActive }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating voucher status', error);
    return { success: false, message: 'Failed to update voucher status' };
  }
};

export const createVoucher = async (payload: Record<string, any>) => {
  try {
    const res = await api.post<{ success: boolean; message?: string; data?: any }>(
      '/vouchers',
      payload
    );

    return res.data;
  } catch (error: any) {
    console.error('Error creating voucher', error);
    return { success: false, message: error?.response?.data?.message || error.message };
  }
};

export const getVoucher = async (voucherCode: string): Promise<VoucherDetailResponse | null> => {
  try {
    const res = await api.get<{ success: boolean; message?: string; data?: any }>(
      `/vouchers/${voucherCode}`
    );
    const resData = res.data;
    return {
      success: resData.success,
      message: resData.message ?? '',
      data: resData.data,
    };
  } catch (error: any) {
    console.error('Error fetching voucher', error);
    return null;
  }
};

export const updateVoucher = async (voucherCode: string, payload: Record<string, any>) => {
  try {
    const res = await api.put<{ success: boolean; message?: string; data?: any }>(
      `/vouchers/${voucherCode}`,
      payload
    );
    return res.data;
  } catch (error: any) {
    console.error('Error updating voucher', error);
    return { success: false, message: error?.response?.data?.message || error.message };
  }
};

export const deleteVoucher = async (voucherCode: string) => {
  try {
    const res = await api.delete<{ success: boolean; message?: string }>(
      `/vouchers/${voucherCode}`
    );
    return res.data;
  } catch (error: any) {
    console.error('Error deleting voucher', error);
    return { success: false, message: error?.response?.data?.message || error.message };
  }
};

export const validateVoucher = async (email: string, voucherCode: string) => {
  try {
    const res = await api.post<{ success: boolean; message?: string; data?: any }>(
      `/vouchers/validate`,
      { email, voucher_code: voucherCode }
    );

    return res.data;
  } catch (error: any) {
    console.error('Error validating voucher', error);
    // try to return structured error if available
    const message =
      error?.response?.data?.message || error?.message || 'Failed to validate voucher';
    return { success: false, message } as { success: false; message: string };
  }
};
